import dayjs from 'dayjs';
import { useInfoByStatus } from './useInfoByStatus';
import {
  EStatusAppointment,
  TStatusCamel,
  TAppointmentCamel,
  TNullable,
} from '@/types';
import { SYSTEM_ID } from '@/utils/constants';

interface IAppointmentBasicInfo {
  lastStatus: TNullable<TStatusCamel>;
  rescheduledDate: TNullable<{ start: string; end: string }>;
  isAppointmentEnded: boolean;
  isCanceled: boolean;
  isExpired: boolean;
  isFutureAppointment: boolean;
  isDeclined: boolean;
  startTime: string;
  endTime: string;
}

/**
 * Custom hook to extract and compute appointment information based on status and timing.
 * This hook determines:
 * - The last status of the appointment
 * - Rescheduled date information (if applicable)
 * - Whether the appointment has ended (based on end time)
 * - Whether the appointment is canceled (explicitly or implicitly due to past start time with inactive status)
 * @param appointment - Appointment object containing start time, end time, and status history.
 *                      Can be null or undefined if no appointment is selected.
 * @returns Object containing:
 *   - lastStatus: The recent status from the appointment's status history, or null if none exists
 *   - rescheduledDate: New start/end times if the appointment is pending confirmation with rescheduled dates, otherwise null
 *   - isAppointmentEnded: True if the appointment's end time (or rescheduled end time) is in the past
 *   - isCanceled: True if the appointment is explicitly canceled OR if the start time has passed without an active status
 *   - isExpired: True if the appointment was auto-cancelled by the system (last status `canceled` by the system sentinel), as opposed to a user cancellation
 *   - isFutureAppointment: True if the appointment's start time (or rescheduled start time) is in the future
 *   - isDeclined: True if the appointment was declined (previous status was pending confirmation or pending insurance by a different actor)
 *   - startTime: It is checked whether reschedule exists; if so, the date with reschedule.start is preferred; if it does not exist, appointment.start is selected.
 *   - endTime: It is checked whether reschedule exists; if so, the date with reschedule.end is preferred; if it does not exist, appointment.end is selected.
 */
export const useInfoByAppointment = (
  appointment?: TNullable<TAppointmentCamel>,
): IAppointmentBasicInfo => {
  const { lastStatus, rescheduledDate } = useInfoByStatus(appointment?.status);

  const startTime = rescheduledDate?.start || appointment?.start || '';
  const endTime = rescheduledDate?.end || appointment?.end || '';

  const isAppointmentEnded = dayjs(endTime).isBefore();
  const isFutureAppointment = dayjs().isBefore(endTime);

  const isCanceled = lastStatus?.action === EStatusAppointment.CANCELED;

  const isExpired = isCanceled && lastStatus?.by === SYSTEM_ID;

  // this block checks if the appointment was declined:
  // An appointment is considered declined if its last status is 'canceled'
  // and the previous status was 'pending confirmation' or 'pending insurance' by a different actor.
  // ATTENTION: If this logic extends decompose it into a separate hook, e.g. useAppointmentDecline, to avoid mixing concerns and improve readability.
  const preLastStatus = appointment?.status.at(-2);
  const isPrelastStatusPendingConfirmationOrInsurance = [
    EStatusAppointment.PENDING_CONFIRMATION,
    EStatusAppointment.PENDING_INSURANCE,
  ].some((s) => s === preLastStatus?.action);
  const isPrelastStatusByDifferentActor =
    preLastStatus && lastStatus ? preLastStatus.by !== lastStatus.by : false;
  const isDeclined =
    isCanceled &&
    isPrelastStatusPendingConfirmationOrInsurance &&
    isPrelastStatusByDifferentActor;

  return {
    lastStatus,
    rescheduledDate,
    isAppointmentEnded,
    isCanceled,
    isExpired,
    isFutureAppointment,
    isDeclined,
    startTime,
    endTime,
  };
};
